import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { message } from 'antd';
import * as Yup from 'yup';
import { addUser, getPincodeList, getListAction, createTripV2 } from 'store/pageSlice';
import { createConsignmentNoteV2 } from 'store/tripSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import CreateLRForm from './CreateLRForm';
const { getServerFormatedDate, getServerFormatedDateV2 } = require('utils/helpers'); // or use import if ES6

// Constants
const ERROR_CREATING_LR = 'Error creating LR';
const INTERCITY_TRIPS_ROUTE = '/trips/intercity';

// LR Creation Validation Schema
const LRValidationSchema = Yup.object({
	goods_description: Yup.string().required().min(3).max(32).label('Good Description'),
	declared_value: Yup.number().required().label('Declared Value'),
	unit_count: Yup.number().label('Unit Count'),
	sender_address: Yup.string().required().min(3).max(200).label('Sender Address'),
	sender_name: Yup.string().required().min(3).max(128).label('Sender Name'),
	sender_pincode: Yup.string()
		.required('Pincode is required')
		.nullable()
		.min(6)
		.max(6)
		.label('Sender Pincode'),
	source_location: Yup.string().required().label('Source Location'),
	receiver_address: Yup.string().required().min(3).max(200).label('Receiver Address'),
	receiver_name: Yup.string().required().min(3).max(128).label('Receiver Name'),
	ewb_number: Yup.string()
		.min(12)
		.max(12)
		.matches(/^[0-9]*$/, 'Invalid EWB. Only Numbers Allowed.')
		.label('Ewb Number'),
	client_reference_numbers: Yup.array()
		.of(Yup.string().required('Client Reference Number is required'))
		.required('At least one Client Reference Number is required')
		.min(1, 'At least one Client Reference Number is required')
		.label('Client Reference Number'),
	receiver_pincode: Yup.string()
		.required('Pincode is required')
		.nullable()
		.min(6)
		.max(6)
		.label('Receiver Pincode'),
	destination_location: Yup.string().required().label('Destination Location'),
	gross_weight: Yup.number().required().label('Gross Weight'),
	goods_details: Yup.array().of(
		Yup.object().shape({
			goods_description: Yup.string()
				.required('Good Description is required')
				.label('Good Description'),
			goods_height: Yup.string().label('Goods Height'),
			goods_length: Yup.string().label('Goods Length'),
			goods_quantity: Yup.string().label('Goods Quantity').nullable(),
			goods_uom: Yup.string().label('Goods UOM').nullable(),
			goods_weight: Yup.string().label('Goods Weight'),
			goods_width: Yup.string().label('Goods Width'),
		})
	),
});

function getTripFormFromQuery(location) {
	const params = new URLSearchParams(location.search);
	const data = params.get('data');
	if (data) {
		try {
			return JSON.parse(decodeURIComponent(data));
		} catch {
			return {};
		}
	}
	return {};
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function CreateLR() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const { id } = useParams();
	const [enrichedTripForm, setEnrichedTripForm] = useState(null);
	const tripForm = getTripFormFromQuery(location);
	const formRef = useRef(); // Add ref to access form data

	// Check if we're on create-lr without ID (for form data persistence)
	const isCreateLRWithoutId = location.pathname === '/trips/create-lr' && !id;

	// Detect edit mode from location state
	const isEditMode = location.state?.isEdit || false;

	// Handle back navigation with form data persistence
	const handleBackNavigation = (formValues) => {
		if (isCreateLRWithoutId) {
			if (location.state?.from) {
				navigate(location.state.from, {
					state: {
						formData: formValues,
						source: 'create-lr',
					},
				});
			} else {
				navigate(-1, {
					state: {
						formData: formValues,
						source: 'create-lr',
					},
				});
			}
		} else {
			// Regular navigation for other cases
			navigate(location.state?.from || -1);
		}
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		async function fetchAndEnrich() {
			if (
				id &&
				(!tripForm.vendor_id ||
					!tripForm.project_id ||
					!tripForm.driver_name ||
					!tripForm.vehicle_number)
			) {
				const res = await dispatch(getListAction({ search_type: 'trip', id }));
				const trip = res?.rows?.[0] || {};

				// Map trip pincode data to LR form fields
				const sourcePincode = trip.source_pincode || '';
				const destinationPincode = trip.destination_pincode || '';

				// Fetch city and state information for the pincodes
				let sourceLocation = '';
				let destinationLocation = '';

				if (sourcePincode) {
					try {
						const sourceRes = await dispatch(getPincodeList(sourcePincode));
						const sourceData = sourceRes?.rows?.[0];
						sourceLocation = sourceData
							? `${sourceData.city || ''}${
									sourceData.city && sourceData.state ? ', ' : ''
							  }${sourceData.state || ''}`
							: '';
					} catch (error) {
						console.log('Error fetching source pincode data:', error);
					}
				}

				if (destinationPincode) {
					try {
						const destRes = await dispatch(getPincodeList(destinationPincode));
						const destData = destRes?.rows?.[0];
						destinationLocation = destData
							? `${destData.city || ''}${
									destData.city && destData.state ? ', ' : ''
							  }${destData.state || ''}`
							: '';
					} catch (error) {
						console.log('Error fetching destination pincode data:', error);
					}
				}

				setEnrichedTripForm({
					...tripForm,
					vendor_id: trip.vendor_id || trip.info?.vendor_id || tripForm.vendor_id,
					project_id: trip.project_id || trip.info?.project_id || tripForm.project_id,
					driver_id: trip.driver_id || tripForm.driver_id || undefined,
					driver_name:
						trip.driver_name || trip.info?.driver_name || tripForm.driver_name || '',
					driver_number:
						trip.driver_number ||
						trip.info?.driver_number ||
						tripForm.driver_number ||
						'',
					vehicle_type_id: trip.vehicle_type_id || tripForm.vehicle_type_id || undefined,
					vehicle_number: trip.vehicle_number || tripForm.vehicle_number,
					// Map pincode data from trip to LR form
					sender_pincode: sourcePincode,
					source_location: sourceLocation || tripForm.source_location || '',
					receiver_pincode: destinationPincode,
					destination_location:
						destinationLocation || tripForm.destination_location || '',
				});
			} else {
				// Handle case where trip form data comes from query parameters
				// Enrich pincode data with city/state information
				const sourcePincode = (
					tripForm.start_location_pincode ||
					tripForm.sender_pincode ||
					''
				).toString();
				const destinationPincode = (
					tripForm.end_location_pincode ||
					tripForm.receiver_pincode ||
					''
				).toString();

				// Fetch city and state information for the pincodes
				let sourceLocation = '';
				let destinationLocation = '';

				if (sourcePincode && sourcePincode !== '') {
					try {
						const sourceRes = await dispatch(getPincodeList(sourcePincode));
						const sourceData = sourceRes?.rows?.[0];
						sourceLocation = sourceData
							? `${sourceData.city || ''}${
									sourceData.city && sourceData.state ? ', ' : ''
							  }${sourceData.state || ''}`
							: '';
					} catch (error) {
						console.log('Error fetching source pincode data:', error);
					}
				}

				if (destinationPincode && destinationPincode !== '') {
					try {
						const destRes = await dispatch(getPincodeList(destinationPincode));
						const destData = destRes?.rows?.[0];
						destinationLocation = destData
							? `${destData.city || ''}${
									destData.city && destData.state ? ', ' : ''
							  }${destData.state || ''}`
							: '';
					} catch (error) {
						console.log('Error fetching destination pincode data:', error);
					}
				}

				setEnrichedTripForm({
					...tripForm,
					// Preserve driver fields from trip form
					driver_id: tripForm.driver_id || undefined,
					driver_name: tripForm.driver_name || '',
					driver_number: tripForm.driver_number || '',
					vehicle_type_id: tripForm.vehicle_type_id || undefined,
					// Map pincode data from trip form to LR form
					sender_pincode: sourcePincode,
					source_location: sourceLocation || tripForm.source_location || '',
					receiver_pincode: destinationPincode,
					destination_location:
						destinationLocation || tripForm.destination_location || '',
				});
			}
		}
		fetchAndEnrich();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, location.search]);

	if (!enrichedTripForm) return null; // or a loader
	const initialValues = {
		...enrichedTripForm,
		goods_description: enrichedTripForm.goods_description || '',
		declared_value: enrichedTripForm.declared_value || '',
		gross_weight: enrichedTripForm.gross_weight || '',
		unit_count: enrichedTripForm.unit_count || '',
		client_reference_numbers: enrichedTripForm.client_reference_numbers || [],
		ewb_number: enrichedTripForm.ewb_number || '',
		sender_name: enrichedTripForm.sender_name || '',
		sender_address: enrichedTripForm.sender_address || '',
		sender_pincode: enrichedTripForm.sender_pincode || '',
		source_location: enrichedTripForm.source_location || '',
		receiver_name: enrichedTripForm.receiver_name || '',
		receiver_address: enrichedTripForm.receiver_address || '',
		receiver_pincode: enrichedTripForm.receiver_pincode || '',
		destination_location: enrichedTripForm.destination_location || '',
		goods_details: enrichedTripForm.goods_details || [
			{
				goods_description: '',
				goods_quantity: '',
				goods_uom: '',
				goods_length: '',
				goods_width: '',
				goods_height: '',
				goods_weight: '',
			},
		],
	};

	// Helper function to set location field based on type
	const setLocationField = (type, setFieldValue, value) => {
		const fieldName = type === 'source' ? 'source_location' : 'destination_location';
		setFieldValue(fieldName, value);
	};

	// Helper function to handle pincode API response
	const handlePincodeResponse = (data, type, setFieldValue) => {
		const cityState = data
			? `${data.city || ''}${data.city && data.state ? ', ' : ''}${data.state || ''}`
			: '';
		setLocationField(type, setFieldValue, cityState);
	};

	// Handler for pincode autofill
	const handlePincodeChange = async (pincode, type, setFieldValue) => {
		if (!pincode || pincode.length !== 6) {
			setLocationField(type, setFieldValue, '');
			return;
		}

		try {
			const res = await dispatch(getPincodeList(pincode));
			const data = res?.rows?.[0];
			handlePincodeResponse(data, type, setFieldValue);
		} catch {
			setLocationField(type, setFieldValue, '');
		}
	};

	const validateEwbNumber = (value) => {
		if (!value) return undefined;
		if (!/^\d{12}$/.test(value)) return 'Invalid EWB number format';
		return undefined;
	};

	// Helper: Build V2 consignment note data payload
	const buildV2ConsignmentData = (tripFormValues, tripId, isEdit) => ({
		...(isEdit && tripFormValues.consignment_note_id
			? { id: tripFormValues.consignment_note_id }
			: {}),
		trip_id: tripId,
		project_id: tripFormValues.project_id,
		vendor_id: tripFormValues.vendor_id,
		sender_name: tripFormValues.sender_name,
		sender_address: tripFormValues.sender_address,
		sender_pincode: tripFormValues.sender_pincode,
		source_location: tripFormValues.source_location,
		receiver_name: tripFormValues.receiver_name,
		receiver_address: tripFormValues.receiver_address,
		receiver_pincode: tripFormValues.receiver_pincode,
		destination_location: tripFormValues.destination_location,
		client_reference_numbers: tripFormValues.client_reference_numbers,
		ewb_number: tripFormValues.ewb_number,
		driver_name: tripFormValues.driver_name,
		vehicle_number: tripFormValues.vehicle_number,
		goods_description: tripFormValues.goods_description,
		declared_value: tripFormValues.declared_value,
		gross_weight: tripFormValues.gross_weight,
		unit_count: tripFormValues.unit_count,
		goods_details: tripFormValues.goods_details,
	});

	// Helper: Build V1 consignment note payload
	const buildV1ConsignmentPayload = (tripFormValues, tripId, isEdit) => ({
		...(isEdit ? { update_type: 'consignment_note' } : { create_type: 'consignment_note' }),
		data: {
			id: tripFormValues.consignment_note_id || '',
			trip_id: tripId,
			project_id: tripFormValues.project_id,
			vendor_id: tripFormValues.vendor_id,
			sender_name: tripFormValues.sender_name,
			sender_address: tripFormValues.sender_address,
			sender_pincode: tripFormValues.sender_pincode,
			source_location: tripFormValues.source_location,
			receiver_name: tripFormValues.receiver_name,
			receiver_address: tripFormValues.receiver_address,
			receiver_pincode: tripFormValues.receiver_pincode,
			destination_location: tripFormValues.destination_location,
			client_reference_numbers: tripFormValues.client_reference_numbers,
			ewb_number: tripFormValues.ewb_number,
			driver_name: tripFormValues.driver_name,
			vehicle_number: tripFormValues.vehicle_number,
			goods_description: tripFormValues.goods_description,
			declared_value: tripFormValues.declared_value,
			gross_weight: tripFormValues.gross_weight,
			unit_count: tripFormValues.unit_count,
			goods_details: tripFormValues.goods_details,
		},
	});

	// Helper: Handle consignment note creation (when trip ID exists)
	const handleConsignmentNoteCreation = async (tripFormValues, tripId, useV2API) => {
		if (useV2API) {
			// V2 API: Use createConsignmentNoteV2
			const consignmentData = buildV2ConsignmentData(tripFormValues, tripId, isEditMode);
			await dispatch(
				createConsignmentNoteV2({
					consignmentData,
					setErrors: () => {},
					isEditMode,
				})
			);
		} else {
			// V1 API: Use existing addUser flow
			const lrPayload = buildV1ConsignmentPayload(tripFormValues, tripId, isEditMode);
			await dispatch(
				addUser({
					data: lrPayload,
					setErrors: () => {},
					isEditMode,
				})
			);
		}
	};

	// Helper: Create trip with LR using V2 API
	const createTripWithLRV2 = async (
		tripFormValues,
		trip_info,
		reporting_date_payload,
		reporting_time_payload,
		start_date_payload,
		start_time_payload
	) => {
		// Step 1: Build trip payload (without LR fields)
		// Resolve vehicle_type_id if vehicle_type is present
		let vehicleTypeId = null;
		if (tripFormValues.vehicle_type_id) {
			vehicleTypeId = tripFormValues.vehicle_type_id;
		}

		const tripPayload = {
			trip_type: tripFormValues.trip_type,
			city: tripFormValues.city,
			project_id: tripFormValues.project_id,
			vendor_id: tripFormValues.vendor_id,
			vehicle_type: tripFormValues.vehicle_type,
			...(vehicleTypeId ? { vehicle_type_id: vehicleTypeId } : {}),
			vehicle_number: tripFormValues.vehicle_number,
			driver_id: tripFormValues.driver_id ? tripFormValues.driver_id : undefined,
			driver_name: tripFormValues.driver_name || '',
			driver_number: tripFormValues.driver_number || '',
			reporting_date: reporting_date_payload,
			reporting_time: `${reporting_date_payload} ${reporting_time_payload}`,
			start_date: start_date_payload,
			start_time: `${start_date_payload} ${start_time_payload}`,
			destination_pincode: tripFormValues.end_location_pincode,
			source_pincode: tripFormValues.start_location_pincode,
			source_location: tripFormValues.source_location || '',
			destination_location: tripFormValues.destination_location || '',
			info: trip_info,
			...(tripFormValues.adhoc_request_id && {
				adhoc_request_id: tripFormValues.adhoc_request_id,
			}),
		};

		// Step 2: Create trip using V2 API
		const tripResponse = await dispatch(
			createTripV2({
				tripData: tripPayload,
				setErrors: () => {},
				isEditMode: false,
			})
		);

		if (!tripResponse || tripResponse.status !== 'success' || !tripResponse.data?.id) {
			throw new Error('Failed to create trip');
		}

		const createdTripId = tripResponse.data.id;

		// Step 3: Create consignment note using V2 API with the created trip_id
		const consignmentData = buildV2ConsignmentData(tripFormValues, createdTripId, false);

		await dispatch(
			createConsignmentNoteV2({
				consignmentData,
				setErrors: () => {},
				isEditMode: false,
			})
		);
	};

	// Helper: Create trip with LR using V1 API
	const createTripWithLRV1 = async (
		tripFormValues,
		trip_info,
		reporting_date_payload,
		reporting_time_payload,
		start_date_payload,
		start_time_payload
	) => {
		// Resolve vehicle_type_id if vehicle_type is present
		let vehicleTypeId = null;
		if (tripFormValues.vehicle_type_id) {
			vehicleTypeId = tripFormValues.vehicle_type_id;
		}

		const payload = {
			trip_type: tripFormValues.trip_type,
			city: tripFormValues.city,
			project_id: tripFormValues.project_id,
			vendor_id: tripFormValues.vendor_id,
			vehicle_type: tripFormValues.vehicle_type,
			...(vehicleTypeId ? { vehicle_type_id: vehicleTypeId } : {}),
			vehicle_number: tripFormValues.vehicle_number,
			driver_id: tripFormValues.driver_id ? tripFormValues.driver_id : undefined,
			driver_name: tripFormValues.driver_name,
			driver_number: tripFormValues.driver_number || '',
			reporting_date: reporting_date_payload,
			reporting_time: `${reporting_date_payload} ${reporting_time_payload}`,
			start_date: start_date_payload,
			start_time: `${start_date_payload} ${start_time_payload}`,
			destination_pincode: tripFormValues.end_location_pincode,
			source_pincode: tripFormValues.start_location_pincode,
			source_location: tripFormValues.source_location || '',
			destination_location: tripFormValues.destination_location || '',
			sender_name: tripFormValues.sender_name,
			sender_address: tripFormValues.sender_address,
			sender_pincode: tripFormValues.sender_pincode,
			receiver_name: tripFormValues.receiver_name,
			receiver_address: tripFormValues.receiver_address,
			receiver_pincode: tripFormValues.receiver_pincode,
			client_reference_numbers: tripFormValues.client_reference_numbers,
			ewb_number: tripFormValues.ewb_number,
			goods_description: tripFormValues.goods_description,
			declared_value: tripFormValues.declared_value,
			gross_weight: tripFormValues.gross_weight,
			unit_count: tripFormValues.unit_count,
			goods_details: tripFormValues.goods_details,
			info: trip_info,
			create_lr: true,
			...(tripFormValues.adhoc_request_id && {
				adhoc_request_id: tripFormValues.adhoc_request_id,
			}),
		};

		const submitPayload = {
			data: payload,
			create_type: 'trip',
		};

		await dispatch(
			addUser({
				data: submitPayload,
				setErrors: () => {},
				isEditMode,
			})
		);
	};

	const handleSubmit = async (values, { setSubmitting }) => {
		const { _refInput, ...cleanedValues } = values;
		const tripFormValues = { ...enrichedTripForm, ...cleanedValues };

		// Check if V2 UI is active
		const useV2API = getUIVersion() === 'new';

		if (id) {
			// If id is present, create LR (consignment_note) for the trip
			try {
				await handleConsignmentNoteCreation(tripFormValues, id, useV2API);
				navigate(INTERCITY_TRIPS_ROUTE);
			} catch (err) {
				message.error(ERROR_CREATING_LR);
			} finally {
				if (setSubmitting) {
					setSubmitting(false);
				}
			}
			return;
		}

		const trip_info = {
			client_rate: Number(tripFormValues.client_rate),
			vendor_rate: Number(tripFormValues.vendor_rate),
			is_inter_city: true,
			driver_name: tripFormValues.driver_name || '',
			driver_number: tripFormValues.driver_number || '',
			requirement_proof:
				Array.isArray(tripFormValues.requirement_proof) &&
				tripFormValues.requirement_proof.length > 0
					? tripFormValues.requirement_proof
					: [''],
		};

		const dateFormatter =
			getUIVersion() === 'new' ? getServerFormatedDateV2 : getServerFormatedDate;

		const { reporting_date_payload, reporting_time_payload } = dateFormatter(
			tripFormValues.reporting_date,
			'reporting_date'
		);
		const { start_date_payload, start_time_payload } = dateFormatter(
			tripFormValues.start_date,
			'start_date'
		);

		try {
			if (useV2API) {
				// V2 API: Create trip first, then create consignment note
				await createTripWithLRV2(
					tripFormValues,
					trip_info,
					reporting_date_payload,
					reporting_time_payload,
					start_date_payload,
					start_time_payload
				);
			} else {
				// V1 API: Create trip with LR in one call
				await createTripWithLRV1(
					tripFormValues,
					trip_info,
					reporting_date_payload,
					reporting_time_payload,
					start_date_payload,
					start_time_payload
				);
			}
			message.success('Trip created successfully with LR');
			navigate(INTERCITY_TRIPS_ROUTE, { state: { refresh: true } });
		} catch (err) {
			message.error(ERROR_CREATING_LR);
		} finally {
			if (setSubmitting) {
				setSubmitting(false);
			}
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			onSubmit={handleSubmit}
			validationSchema={LRValidationSchema}
		>
			{(formikProps) => (
				<CreateLRForm
					{...formikProps}
					handlePincodeChange={handlePincodeChange}
					validateEwbNumber={validateEwbNumber}
					handleBackNavigation={handleBackNavigation}
					formRef={formRef}
					isCreateLRWithoutId={isCreateLRWithoutId}
					isEditMode={isEditMode}
				/>
			)}
		</Formik>
	);
}
